import _ from 'lodash';
import qmsService, { parseQmsSignedUrl, toQmsPublicImageUrlFromSignedUrl } from '../../services/qmsService';
import QmsRow from '../../model/qms';
import QmsEscalationMatrix from '../../model/escalationMatrix';
import { normalizeQmsListQuery } from '../../helper/qmsListQuery';

const state = {
  baseUrl: '/qms',
  pagination: {},
  rows: [],
  loading: false,
  one: null,
  escalationMatrix: null,
  listByRole: [],
  paginationByRole: {},
  qmsUserFromHono: {},
  responseTemplates: []
};

const actions = {
  list({ dispatch, commit }, { query = {}, router }) {
    dispatch('alert/clear', {}, { root: true });
    commit('setRows', { rows: [], pagination: {} });
    commit('startRequest');

    const listQuery = normalizeQmsListQuery(query);

    qmsService
      .list({ query: listQuery })
      .then(response => {
        const data = response || {};
        const rows = data.rows || (data.data && data.data.rows) || [];
        const pagination = data.pagination || (data.data && data.data.pagination) || {};
        commit('setRows', { rows, pagination });
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
      });
  },
  listByRole({ dispatch, commit }, { query = {}, router }) {
    dispatch('alert/clear', {}, { root: true });
    commit('setRows', { rows: [], pagination: {} });
    commit('startRequest');

    const listQuery = normalizeQmsListQuery(query);
    delete listQuery.isQMSAdmin;

    qmsService
      .getQmsListByRole({ query: listQuery })
      .then(response => {
        const data = response || {};
        const rows = data.rows || (data.data && data.data.rows) || [];
        const pagination = data.pagination || (data.data && data.data.pagination) || {};
        commit('setRows', { rows, pagination });
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
      });
  },
  listAdmin({ dispatch, commit }, { query = {}, router }) {
    dispatch('alert/clear', {}, { root: true });
    commit('setRows', { rows: [], pagination: {} });
    commit('startRequest');

    const listQuery = normalizeQmsListQuery(query);
    delete listQuery.isQMSAdmin;

    qmsService
      .getQmsAdminList({ query: listQuery })
      .then(response => {
        const data = response || {};
        const rows = data.rows || (data.data && data.data.rows) || [];
        const pagination = data.pagination || (data.data && data.data.pagination) || {};
        commit('setRows', { rows, pagination });
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
      });
  },
  listResponseTemplates({ dispatch, commit }, { router }) {
    commit('startRequest');
    return qmsService.listResponseTemplates()
      .then(response => {
        const data = response || {};
        const rows = Array.isArray(data) ? data : data.rows || data.responses || data.templates || [];
        commit('setResponseTemplates', { rows });
        return rows;
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
        throw e;
      });
  },
  saveResponseTemplates({ dispatch, commit }, { payload, edit, router }) {
    commit('startRequest');
    const request = edit ? qmsService.patchResponseTemplate({ payload }) : qmsService.postResponseTemplates({ payload });
    return request.then(response => {
      commit('requestFinished');
      dispatch('alert/success', { showType: 'toast', position: 'center', title: '', text: edit ? 'Closure remark updated.' : 'Closure remarks created.' }, { root: true });
      router.push('/qms/closure-remarks');
      return response;
    }).catch(e => {
      commit('requestFailed');
      dispatch('common/handleServiceException', { e, router }, { root: true });
      throw e;
    });
  },
  deleteResponseTemplate({ dispatch, commit }, { templateId, router }) {
    commit('startRequest');
    return qmsService.deleteResponseTemplate({ templateId }).then(() => {
      dispatch('alert/success', { showType: 'toast', position: 'center', title: '', text: 'Closure remark deleted.' }, { root: true });
      return dispatch('listResponseTemplates', { router });
    }).catch(e => {
      commit('requestFailed');
      dispatch('common/handleServiceException', { e, router }, { root: true });
      throw e;
    });
  },
  listEscalationMatrix({ dispatch, commit }, { router, key = 'hr_management' }) {
    dispatch('alert/clear', {}, { root: true });
    commit('clearEscalationMatrix');
    commit('startRequest');

    return qmsService
      .getEscalationMatrix({ key })
      .then(response => {
        const escalationMatrix = new QmsEscalationMatrix(response || {});
        commit('setEscalationMatrix', { escalationMatrix });
        return response;
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
        throw e;
      });
  },
  postEscalationMatrix(
    { dispatch, commit },
    { payload, router, key = 'hr_management', redirectUrl = '' }
  ) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');

    return qmsService
      .postEscalationMatrix({ key, payload })
      .then(response => {
        const escalationMatrix = new QmsEscalationMatrix(response || {});
        commit('setEscalationMatrix', { escalationMatrix });
        dispatch(
          'alert/success',
          {
            showType: 'toast',
            position: 'center',
            title: '',
            text: 'Escalation matrix saved.'
          },
          { root: true }
        );
        if (redirectUrl !== '') {
          router.push(redirectUrl);
        }
        return response;
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
        throw e;
      });
  },
  patchEscalationMatrix(
    { dispatch, commit },
    { payload, router, key = 'hr_management', redirectUrl = '' }
  ) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');

    return qmsService
      .patchEscalationMatrix({ key, payload })
      .then(response => {
        const escalationMatrix = new QmsEscalationMatrix(response || {});
        commit('setEscalationMatrix', { escalationMatrix });
        dispatch(
          'alert/success',
          {
            showType: 'toast',
            position: 'center',
            title: '',
            text: 'Escalation matrix updated.'
          },
          { root: true }
        );
        if (redirectUrl !== '') {
          router.push(redirectUrl);
        }
        return response;
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
        throw e;
      });
  },
  getOne({ dispatch, commit }, { id, router }) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');
    commit('setOne', { doc: null });

    qmsService
      .getOne({ id })
      .then(response => {
        commit('setOne', { doc: response });
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
      });
  },
  getOneQmsUserFromHono({ dispatch, commit }, { id, router, silent = false }) {
    if (!silent) {
      dispatch('alert/clear', {}, { root: true });
      commit('startRequest');
    }
    commit('setOneQmsUserFromHono', { data: null });

    return qmsService
      .getOneQmsUserFromHono({ id })
      .then(response => {
        commit('setOneQmsUserFromHono', { data: response });
        return response;
      })
      .catch(e => {
        commit('requestFailed');
        if (!silent) {
          dispatch('common/handleServiceException', { e, router }, { root: true });
        }
        throw e;
      });
  },
  postOne({ dispatch, commit }, { payload, router, redirectUrl = '' }) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');

    return qmsService
      .postOne({ payload })
      .then(response => {
        commit('requestFinished');
        dispatch(
          'alert/success',
          {
            showType: 'toast',
            position: 'center',
            title: '',
            text: 'Query has been submitted.'
          },
          { root: true }
        );
        if (redirectUrl !== '') {
          router.push(redirectUrl);
        }
        return response;
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
        throw e;
      });
  },
  patchOne({ dispatch, commit }, { id, payload, router, redirectUrl = '' }) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');

    qmsService
      .patchOne({ id, payload })
      .then(() => {
        dispatch(
          'alert/success',
          {
            showType: 'toast',
            position: 'center',
            title: '',
            text: 'Query has been updated.'
          },
          { root: true }
        );
        if (redirectUrl !== '') {
          router.push(redirectUrl);
        }
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
      });
  },
  patchOneByRole({ dispatch, commit }, { id, payload, router, redirectUrl = '' }) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');

    qmsService
      .patchOneByRole({ id, payload })
      .then(() => {
        dispatch(
          'alert/success',
          {
            showType: 'toast',
            position: 'center',
            title: '',
            text: 'Query has been updated.'
          },
          { root: true }
        );
        if (redirectUrl !== '') {
          router.push(redirectUrl);
        }
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
      });
  },
  deleteOne({ dispatch, commit, rootGetters }, { id, router }) {
    dispatch('alert/clear', {}, { root: true });
    commit('startRequest');

    qmsService
      .deleteOne({ id })
      .then(() => {
        const listQuery = normalizeQmsListQuery(router.currentRoute.query || {});
        const authGetter = rootGetters && rootGetters['auth/isLoggedIn'];
        const isLoggedIn = typeof authGetter === 'function' ? authGetter() : Boolean(authGetter);
        const isOtpSession =
          typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('qmsOtpVerified') === 'true' &&
          !isLoggedIn;
        let listAction = 'listByRole';
        if (isOtpSession) {
          listAction = 'list';
        } else if (router.currentRoute.name === 'qms-admin') {
          listAction = 'listAdmin';
        }
        dispatch(listAction, { query: listQuery, router });
        dispatch(
          'alert/success',
          {
            showType: 'toast',
            position: 'center',
            title: '',
            text: 'Query has been deleted.'
          },
          { root: true }
        );
      })
      .catch(e => {
        commit('requestFailed');
        dispatch('common/handleServiceException', { e, router }, { root: true });
      });
  },
  uploadDocument({ dispatch }, query = {}, router) {
    const { file, fileName } = query;
    return qmsService
      .getSignedUrl({ filename: fileName, filetype: file.type })
      .then(response => {
        const signedUrl = parseQmsSignedUrl(response);
        if (!signedUrl) {
          throw new Error('Signed upload URL was not returned.');
        }
        return qmsService
          .uploadDocument({ url: signedUrl, file })
          .then(() => toQmsPublicImageUrlFromSignedUrl(signedUrl));
      })
      .catch(e => {
        dispatch('common/handleServiceException', { e, router }, { root: true });
        throw e;
      });
  }
};

const mutations = {
  startRequest(state) {
    state.loading = true;
  },
  requestFailed(state) {
    state.loading = false;
  },
  requestFinished(state) {
    state.loading = false;
  },
  setRows(state, { rows, pagination }) {
    let row = 1;
    if (pagination && pagination.first_row_no != null) {
      row = pagination.first_row_no;
    } else if (pagination && pagination.page && pagination.page_size) {
      row = (pagination.page - 1) * pagination.page_size + 1;
    }
    state.rows = _.map(rows, r => {
      // eslint-disable-next-line no-underscore-dangle
      const legacyId = r._id;
      const mapped = new QmsRow({
        id: legacyId,
        rowNum: row,
        employeeCode: r.employeeCode,
        storeCode: r.storeCode,
        employeeName: r.employeeName,
        department: r.department,
        queryType: r.queryType,
        queryDescription: r.queryDescription,
        closureRemarks: r.closureRemarks != null ? r.closureRemarks : r.closure_remarks,
        employeeRefImages: r.employeeRefImages || [],
        closureRefImages: r.closureRefImages || [],
        isClosed: r.isClosed != null ? r.isClosed : r.is_closed,
        closedAt: r.closedAt,
        closedByName: r.closedByName != null ? r.closedByName : r.closed_by_name,
        closedByLevel: r.closedByLevel != null ? r.closedByLevel : r.closed_by_level,
        tatTime: r.tatTime != null ? r.tatTime : r.tat_time,
        submittedByName: r.submittedByName,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt
      });
      row += 1;
      return mapped;
    });
    state.pagination = pagination || {};
    state.loading = false;
  },
  setOne(state, { doc }) {
    state.one = doc;
    state.loading = false;
  },
  setOneQmsUserFromHono(state, { data }) {
    state.qmsUserFromHono = data;
    state.loading = false;
  },
  setResponseTemplates(state, { rows }) {
    state.responseTemplates = rows;
    state.loading = false;
  },
  clearOne(state) {
    state.one = null;
  },
  setEscalationMatrix(state, { escalationMatrix }) {
    state.escalationMatrix = escalationMatrix;
    state.loading = false;
  },
  clearEscalationMatrix(state) {
    state.escalationMatrix = null;
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations
};

